import React, {useRef} from 'react';
import {Link, useSearchParams} from "react-router-dom";
import {Affix, Switch, Tabs} from "antd";
import InfoTooltip from "../../../components/InfoTooltip";
import {useTeamCandidateScope} from "./TeamCandidateScopeProvider";
import LoadingSpinner from "../../../components/LoadingSpinner";
import TeamDisciplinesTable from "./TeamDisciplinesTable";
import TeamMemberOppStatusTable from "./TeamMemberOppStatusTable";
import TeamPhaseDeliverablesTable from "./TeamPhaseDeliverablesTable";
import {CheckOutlined, CloseOutlined} from "@ant-design/icons";
import TeamPercentMarkupForm from "./TeamPercentMarkupForm";
import TeamConfirmPayoutForm from "./TeamConfirmPayoutForm";
import TeamScopeCoverLetterEditTab from "./TeamScopeCoverLetterEditTab";
import TeamScopeProposalEditTab from "./TeamScopeProposalEditTab";

const LaborCostBreakDown = ({type, hours, cost, estCostNote,}) => {
    return (
        <React.Fragment>
            <div className="flex flex-col gap-1 items-start">
                <p className="text-xxs font-semibold">
                    {type} Total Hours
                </p>
                <p className="text-danger text-base">
                    {hours} hours
                </p>
            </div>
            <div className="flex flex-col gap-1 items-start">
                <p className="text-xxs font-semibold flex flex-row gap-1 items-center">
                    <span>
                        {type} Estimated Platform Cost
                    </span>
                    {estCostNote && (
                        <InfoTooltip placement="right" type="dark">
                            <span className="text-xs px-2 py-3 text-white">
                                {estCostNote}
                            </span>
                        </InfoTooltip>
                    )}
                </p>
                <p className="text-danger text-base">
                    {TB.formatCurrency(cost)}
                </p>
            </div>
        </React.Fragment>
    )
}


const ManageTeamScopeTable = ({forAgency,...props}) => {

    const tableRef = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const {
        team,
        isLoading: isLoadingCandidate,
        isDisabled,
        updateTeamScope,
        onUpdateProposal,
        onChange,
        confirmTeamScope,
        project,
        cancelEdit
    } = useTeamCandidateScope();
    const scope = project.normalizeScopeForService();

    if (isLoadingCandidate || !team) {
        return (
            <div className="flex flex-col items-center py-5">
                <LoadingSpinner size="lg"/>
            </div>
        )
    }

    return (
        <div className="flex flex-col items-stretch gap-3">
            <Link to={{pathname: "../", search: '?tab=teams'}} className="btn btn-link btn-link-primary self-start">
                <i className="fas fa-caret-left mr-2 no-underline"/>
                Back to all Teams
            </Link>
            <div className="flex flex-col gap-6 items-stretch relative manage-team-scope-table" ref={tableRef}>
                <Affix>
                    <div
                        className="flex flex-col bg-white items-stretch px-4 py-6 gap-4 border-b border-gray-100 drop-shadow-sm min-h-[100px]">
                        <p className="text-base">
                            Project Scope for Team <span className="text-primary"></span>
                        </p>

                        <div className="flex flex-row items-stretch">

                            <div className="flex-grow flex flex-row justify-start gap-14">
                                {!forAgency && (
                                    <React.Fragment>
                                        <div className="flex flex-col gap-1.5 items-start">
                                            <p className="text-xs font-semibold">
                                                Original Total Hours
                                            </p>
                                            <p className="text-primary text-lg">
                                                {TB.sumBy(scope.phases, 'total_hours')} hours
                                            </p>
                                        </div>
                                        <div className="flex flex-col gap-1.5 items-start">
                                            <p className="text-xs font-semibold flex flex-row gap-1.5 items-center">
                                                <span>
                                                    Original Estimated Client Fee
                                                </span>
                                                <InfoTooltip placement="right" type="dark">
                                                    <span className="text-xs px-2 py-3 text-white">
                                                        Estimated Client Fee is based on the Standard Platform Market Rate for each
                                                        role. The cost includes the project markup fee of 3%.
                                                    </span>
                                                </InfoTooltip>
                                            </p>
                                            <p className="text-primary text-lg">
                                                {TB.formatCurrency(TB.sumBy(scope.phases, 'estimated_cost'))}
                                            </p>
                                        </div>
                                    </React.Fragment>
                                )}
                                <div className="flex flex-col gap-1.5 items-start">
                                    <p className="text-xs font-semibold">
                                        Team Total Hours
                                    </p>
                                    <p className="text-primary text-lg">
                                        {TB.sumBy(team.disciplines, 'total_proposed_team_hours')} hours
                                    </p>
                                </div>
                                <div className="flex flex-col gap-1.5 items-start">
                                    <p className="text-xs font-semibold flex flex-row gap-1.5 items-center">
                                        <span>
                                            Team Payout{!forAgency && ' / Client Fee'}
                                        </span>
                                        {!forAgency && (
                                            <InfoTooltip placement="right" type="dark">
                                                <span className="text-xs px-2 py-3 text-white">
                                                    Client Fee is based on the Standard Hourly Rates for each individual
                                                    based on their assigned role(s). The cost includes the project markup
                                                    fee of {TB.formatNumber(team.job_candidate.percent_markup * 100, 2)}%.
                                                </span>
                                            </InfoTooltip>
                                        )}
                                    </p>
                                    <p className="text-primary text-lg">
                                        {TB.formatCurrency(TB.sumBy(team.disciplines, 'estimated_proposed_team_cost'))}
                                        {!forAgency && (
                                            <React.Fragment>
                                                &nbsp;/&nbsp;
                                                {TB.formatCurrency(TB.sumBy(team.disciplines, 'estimated_proposed_platform_cost'))}
                                            </React.Fragment>
                                        )}
                                    </p>
                                </div>
                                <div className="flex flex-col gap-1.5 items-start">
                                    <p className="text-xs font-semibold flex flex-row gap-1.5 items-center">
                                        <span>
                                            Team Scope Confirmed
                                        </span>
                                    </p>
                                    <p className="text-primary text-lg">
                                        <span className={!isDisabled ? '' : 'pointer-events-none'}>
                                            <Switch
                                                size="large"
                                                checkedChildren={<CheckOutlined/>}
                                                unCheckedChildren={<CloseOutlined/>}
                                                checked={team.hasConfirmedScope()}
                                                disabled={!team.canConfirmScope()}
                                                onChange={(checked, e) => {
                                                    if (!team.canConfirmScope()) {
                                                        e.preventDefault();
                                                    }else confirmTeamScope()
                                                }}
                                            />
                                        </span>
                                    </p>
                                </div>

                            </div>
                        </div>
                    </div>
                </Affix>

                <div className="bg-white px-4 py-6 manage-scope-table">

                    {team?.hasConfirmedScope() ? (
                        <div className="flex flex-col items-stretch gap-10">
                            <p className="text-lg font-semibold underline">
                                Team Proposal
                            </p>
                            <Tabs
                                defaultActiveKey="1"
                                type="card"
                                onChange={key => cancelEdit()}
                                items={[
                                    {
                                        label: "Team Assignment",
                                        key: 1,
                                        children: (
                                            <div className="flex flex-col gap-10 items-stretch">
                                                <TeamMemberOppStatusTable hideHeader={forAgency} forAgency={forAgency}/>
                                                <div className="flex flex-row items-center gap-6 justify-between">
                                                    <div className="flex flex-row gap-2 justify-end">
                                                        <p className="text-sm flex flex-row gap-1.5 items-center">
                                                            <span>
                                                                {!forAgency ? "Estimated Team Payout" : "Estimated Project Cost"}
                                                            </span>
                                                            <span
                                                                className="font-semibold">{TB.formatCurrency(TB.sumBy(team.phases, 'estimated_team_cost'), false)}</span>
                                                        </p>
                                                        <TeamConfirmPayoutForm/>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    },
                                    {
                                        label: "Cover Letter",
                                        key: 2,
                                        children: (
                                            <div className="py-3 px-4">
                                                <TeamScopeCoverLetterEditTab/>
                                            </div>
                                        )
                                    },
                                    {
                                        label: "Proposal Document",
                                        key: 3,
                                        children: (
                                            <div className="py-3 px-4">
                                                <TeamScopeProposalEditTab/>
                                            </div>
                                        )
                                    },
                                ]}
                            />
                        </div>
                    ) : (
                        <React.Fragment>
                            <div className="flex flex-row items-center justify-between mb-4">
                                <p className="text-lg font-semibold underline">
                                    Team Assignment
                                </p>
                                {!forAgency && <TeamPercentMarkupForm/>}
                            </div>
                            <TeamDisciplinesTable/>
                        </React.Fragment>
                    )}
                </div>

                <hr/>

                <div className="bg-white px-4 py-6">
                    <p className="text-lg font-semibold underline">
                        Team Scope
                    </p>
                    <div className="flex flex-col gap-10">
                        {TB.map(team.phases, phase => (
                            <div className="bg-white p-0 manage-scope-table" key={phase.id}>
                                <div>
                                    <div className="flex-grow flex flex-row justify-start gap-14">
                                        <div className="flex-grow">
                                            <p className="text-lg mb-1">
                                                Phase #{phase.display_index}: <span
                                                className="font-semibold">{phase.name}</span>
                                            </p>
                                            <p className="mb-5">
                                                {phase.description}
                                            </p>
                                        </div>
                                        {!forAgency && (
                                            <LaborCostBreakDown
                                                type="Original"
                                                cost={phase.estimated_cost}
                                                hours={phase.total_hours}
                                            />
                                        )}
                                        <LaborCostBreakDown
                                            type="Team"
                                            cost={phase.estimated_platform_cost}
                                            hours={phase.total_team_hours}
                                        />
                                    </div>

                                    <TeamPhaseDeliverablesTable forAgency={forAgency} canCustomize={false} record={phase}/>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>


            </div>
        </div>
    );
};

export default ManageTeamScopeTable;